import type { HomeRoomsAndBeds, MapCoordinates } from "@b2bportal/core-types";
import {
  type DateRange,
  type GuestsSelection,
  type HotelStarRating,
  Amenity,
  LodgingHomesAmenityKind,
  type LodgingSelection,
  MealPlanKind,
  StayType,
} from "@b2bportal/lodging-api";
import { HotelSortOptionEnum } from "@overrides/types";
import { default as dayjs, type Dayjs } from "dayjs";
import { toHotelStarRating } from "../availability";
import { HOTEL_URL_PARAM_KEYS } from "../queryParams";

export const URL_DATE_FORMAT = "YYYY-MM-DD" as const;

type BuildSearchParamsArgs = {
  dateRange: DateRange;
  guests: GuestsSelection;
  roomsCount?: number;
};

function defined<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export const buildSearchParams = ({
  dateRange,
  guests,
  roomsCount,
}: BuildSearchParamsArgs) => {
  const searchParams = new URLSearchParams(
    [
      [HOTEL_URL_PARAM_KEYS.FROM_DATE, formatDateForUrl(dateRange?.from)],
      [HOTEL_URL_PARAM_KEYS.UNTIL_DATE, formatDateForUrl(dateRange?.until)],
      guests?.adults
        ? [HOTEL_URL_PARAM_KEYS.ADULTS_COUNT, `${guests.adults}`]
        : null,
      guests?.children?.length
        ? [HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT, `${guests.children.length}`]
        : null,
      ...(guests?.children?.length
        ? guests.children.map((age) => [
            HOTEL_URL_PARAM_KEYS.CHILDREN_AGES,
            `${age}`,
          ])
        : []),
      guests?.pets != null
        ? [HOTEL_URL_PARAM_KEYS.PETS_COUNT, `${guests.pets}`]
        : null,
      roomsCount != null
        ? [HOTEL_URL_PARAM_KEYS.ROOMS_COUNT, roomsCount.toString()]
        : null,
    ].filter(defined)
  );
  return searchParams;
};

export const formatDateForUrl = (
  dateString?: string | Dayjs | null
): (string & WithFormat<typeof URL_DATE_FORMAT>) | "" =>
  dateString
    ? (dayjs(dateString).format(URL_DATE_FORMAT) as string &
        WithFormat<typeof URL_DATE_FORMAT>)
    : "";

export const unifyChildrenAges = (
  childrenAges: number[],
  childrenCount: number
): number[] => {
  return childrenCount > 0
    ? new Array(childrenCount)
        .fill(1)
        .map((_, index) =>
          Number.isInteger(childrenAges[index]) && childrenAges[index] >= 0
            ? childrenAges[index]
            : 1
        )
    : [];
};

const FUNNEL_SEARCH_PARAMS: string[] = [
  HOTEL_URL_PARAM_KEYS.FROM_DATE,
  HOTEL_URL_PARAM_KEYS.UNTIL_DATE,
  HOTEL_URL_PARAM_KEYS.ADULTS_COUNT,
  HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT,
  HOTEL_URL_PARAM_KEYS.PETS_COUNT,
  HOTEL_URL_PARAM_KEYS.ROOMS_COUNT,
];

/**
 * Normalizes a hotel query string into the search parameters that define a
 * shopping funnel, ignoring the destination (e.g. the lodging shop page, where
 * the search can be updated without changing the destination). Filter/map/sort
 * params are excluded so changing them does not rotate the funnel ID. Returns
 * `undefined` when the query does not describe a complete stay search.
 */
export const getHotelShopFunnelSearch = (
  search: string | undefined
): Record<string, string | string[]> | undefined => {
  if (!search) {
    return undefined;
  }

  const queryParams = new URLSearchParams(search);

  if (
    !queryParams.get(HOTEL_URL_PARAM_KEYS.FROM_DATE) ||
    !queryParams.get(HOTEL_URL_PARAM_KEYS.UNTIL_DATE)
  ) {
    return undefined;
  }

  const funnelSearch = FUNNEL_SEARCH_PARAMS.reduce<
    Record<string, string | string[]>
  >((acc, key) => {
    const value = queryParams.get(key);
    if (value != null) {
      acc[key] = value;
    }
    return acc;
  }, {});

  const childrenAges = queryParams.getAll(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES);
  if (childrenAges.length > 0) {
    funnelSearch[HOTEL_URL_PARAM_KEYS.CHILDREN_AGES] = childrenAges;
  }

  return funnelSearch;
};

/**
 * Normalizes a hotel availability path (`.../<destination>?<query>`) into the
 * search parameters that define a shopping funnel, including the destination
 * taken from the last path segment. Returns `undefined` when the path does not
 * describe a complete stay search (e.g. an entry surface with no search).
 */
export const getHotelFunnelSearch = (
  pathAndQuery: string | undefined
): Record<string, string | string[]> | undefined => {
  if (!pathAndQuery) {
    return undefined;
  }

  const queryStart = pathAndQuery.indexOf("?");
  const pathname =
    queryStart >= 0 ? pathAndQuery.slice(0, queryStart) : pathAndQuery;
  const query = queryStart >= 0 ? pathAndQuery.slice(queryStart + 1) : "";

  const destination = decodeURIComponent(
    pathname.split("/").filter(Boolean).pop() ?? ""
  );
  const funnelSearch = getHotelShopFunnelSearch(query);

  if (!destination || funnelSearch === undefined) {
    return undefined;
  }

  return { destination, ...funnelSearch };
};

export interface WithFormat<TFormat extends string> {
  __format: TFormat;
}

export function initPriceRange(urlMin: number, urlMax: number) {
  const min = isNaN(urlMin) ? 0 : urlMin;
  const max = isNaN(urlMax) || urlMax === 0 ? Number.MAX_SAFE_INTEGER : urlMax;
  return {
    min,
    max,
    lowest: 0,
    highest: Number.MAX_SAFE_INTEGER,
  };
}

export enum ViewOption {
  LIST = "list",
  MAP = "map",
}

export function isValidSort(sort: unknown): sort is HotelSortOptionEnum {
  return Object.values(HotelSortOptionEnum).includes(
    sort as HotelSortOptionEnum
  );
}

export function isValidView(sort: unknown): sort is ViewOption {
  return Object.values(ViewOption).includes(sort as ViewOption);
}

export type HotelSearchQueryParamValues = {
  adults: number;
  children: number[];
  rooms: number;
  fromDate: (string & WithFormat<typeof URL_DATE_FORMAT>) | "";
  untilDate: (string & WithFormat<typeof URL_DATE_FORMAT>) | "";
  map:
    | {
        centroid: MapCoordinates | undefined;
        zoom: number;
      }
    | undefined;
  lodgingIds: string[];
  filters: {
    starRating: HotelStarRating[];
    userRating: number;
    amenities: Amenity[];
    filterAmenitiesAnd: boolean;
    freeCancellation: boolean;
    priceRange: { min: number; max: number; lowest: number; highest: number };
    payLater: boolean;
    hotelName?: string;
    mealPlan: MealPlanKind[];
    stayType: StayType[];
    homeAmenities: LodgingHomesAmenityKind[];
    homeRoomsAndBeds?: HomeRoomsAndBeds;
  };
  sortBy: HotelSortOptionEnum;
  view: ViewOption;
  pets?: number;
  lodgingSelection?: LodgingSelection;
};

export const parseLodgingParams = (
  searchParams: URLSearchParams
): HotelSearchQueryParamValues => {
  const count =
    Number(searchParams.get(HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT)) || 0;
  const ages = searchParams
    .getAll(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES)
    .map(Number);
  const children = unifyChildrenAges(ages, count);
  const latlng =
    searchParams
      .get(HOTEL_URL_PARAM_KEYS.LAT_LNG)
      ?.split(",")
      .map(Number)
      .filter((_) => !Number.isNaN(_)) || [];
  const zoom = Number(searchParams.get(HOTEL_URL_PARAM_KEYS.ZOOM)) || 13;

  const amenityMap = Object.keys(Amenity).reduce<
    Record<string, keyof typeof Amenity>
  >((acc, key) => {
    acc[Amenity[key as keyof typeof Amenity].toLowerCase()] =
      key as keyof typeof Amenity;
    return acc;
  }, {});

  const mealPlanMap = Object.keys(MealPlanKind).reduce<
    Record<string, keyof typeof MealPlanKind>
  >((acc, key) => {
    acc[MealPlanKind[key as keyof typeof MealPlanKind].toLowerCase()] =
      key as keyof typeof MealPlanKind;
    return acc;
  }, {});

  const stayTypeMap = Object.keys(StayType).reduce<
    Record<string, keyof typeof StayType>
  >((acc, key) => {
    acc[StayType[key as keyof typeof StayType].toLowerCase()] =
      key as keyof typeof StayType;
    return acc;
  }, {});

  const homeAmenitiesMap = Object.keys(LodgingHomesAmenityKind).reduce<
    Record<string, keyof typeof LodgingHomesAmenityKind>
  >((acc, key) => {
    acc[
      LodgingHomesAmenityKind[
        key as keyof typeof LodgingHomesAmenityKind
      ].toLowerCase()
    ] = key as keyof typeof LodgingHomesAmenityKind;
    return acc;
  }, {});
  const filters = {
    starRating:
      searchParams
        .get(HOTEL_URL_PARAM_KEYS.STAR_RATING)
        ?.split(",")
        .reduce<HotelStarRating[]>((acc, r) => {
          const rating = isNaN(Number(r)) ? null : toHotelStarRating(Number(r));
          if (rating != null) acc.push(rating);
          return acc;
        }, []) ?? [],
    userRating: Number(searchParams.get(HOTEL_URL_PARAM_KEYS.USER_RATING)) || 0,
    amenities:
      searchParams
        .get(HOTEL_URL_PARAM_KEYS.AMENITIES)
        ?.split(",")
        .reduce<(keyof typeof Amenity)[]>((acc, str) => {
          const lowerStr = str.toLowerCase();
          if (amenityMap[lowerStr]) {
            acc.push(amenityMap[lowerStr]);
          }
          return acc;
        }, []) ?? [],
    filterAmenitiesAnd:
      searchParams.get(HOTEL_URL_PARAM_KEYS.FILTER_AMENITIES_AND) === null,
    freeCancellation:
      searchParams.get(HOTEL_URL_PARAM_KEYS.FREE_CANCELLATION) !== null,
    payLater: searchParams.get(HOTEL_URL_PARAM_KEYS.PAY_LATER) !== null,
    priceRange: initPriceRange(
      Number(searchParams.get(HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MIN)),
      Number(searchParams.get(HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MAX))
    ),

    mealPlan:
      searchParams
        .get(HOTEL_URL_PARAM_KEYS.MEAL_PLAN)
        ?.split(",")
        .reduce<(keyof typeof MealPlanKind)[]>((acc, str) => {
          const lowerStr = str.toLowerCase();
          if (mealPlanMap[lowerStr]) {
            acc.push(mealPlanMap[lowerStr]);
          }

          return acc;
        }, []) ?? [],
    stayType:
      searchParams
        .get(HOTEL_URL_PARAM_KEYS.STAY_TYPE)
        ?.split(",")
        .reduce<(keyof typeof StayType)[]>((acc, str) => {
          const lowerStr = str.toLowerCase();
          if (stayTypeMap[lowerStr]) {
            acc.push(stayTypeMap[lowerStr]);
          }

          return acc;
        }, []) ?? [],
    homeAmenities:
      searchParams
        .get(HOTEL_URL_PARAM_KEYS.HOME_AMENITIES)
        ?.split(",")
        .reduce<(keyof typeof LodgingHomesAmenityKind)[]>((acc, str) => {
          const lowerStr = str.toLowerCase();
          if (homeAmenitiesMap[lowerStr]) {
            acc.push(homeAmenitiesMap[lowerStr]);
          }

          return acc;
        }, []) ?? [],
  };

  const sortBy = searchParams.get(HOTEL_URL_PARAM_KEYS.SORT_BY);

  const view = searchParams.get(HOTEL_URL_PARAM_KEYS.VIEW);

  const encodedLodgingSelection = searchParams.get(
    HOTEL_URL_PARAM_KEYS.LODGING_SELECTION
  );
  const lodgingSelection =
    encodedLodgingSelection != null
      ? JSON.parse(decodeURIComponent(encodedLodgingSelection))
      : undefined;

  return {
    adults: Number(searchParams.get(HOTEL_URL_PARAM_KEYS.ADULTS_COUNT)) || 2,
    children,
    ...(searchParams.get(HOTEL_URL_PARAM_KEYS.PETS_COUNT) != null
      ? { pets: Number(searchParams.get(HOTEL_URL_PARAM_KEYS.PETS_COUNT)) }
      : {}),
    rooms: Number(searchParams.get(HOTEL_URL_PARAM_KEYS.ROOMS_COUNT)) || 1,
    fromDate: formatDateForUrl(
      searchParams.get(HOTEL_URL_PARAM_KEYS.FROM_DATE)
    ),
    untilDate: formatDateForUrl(
      searchParams.get(HOTEL_URL_PARAM_KEYS.UNTIL_DATE)
    ),
    map: {
      centroid:
        latlng.length > 1
          ? {
              lat: latlng[0] || 0,
              lng: latlng[1] || 0,
            }
          : undefined,
      zoom: zoom,
    },
    lodgingIds:
      searchParams
        .get(HOTEL_URL_PARAM_KEYS.LODGING_IDS)
        ?.split(",")
        .filter(Boolean) || [],
    filters,
    sortBy: isValidSort(sortBy) ? sortBy : HotelSortOptionEnum.Recommended,
    view: isValidView(view) ? (view as ViewOption) : ViewOption.LIST,
    lodgingSelection,
  };
};
